import {FC, useState} from 'react'
import {useQuery} from 'react-query'
import {useAuth} from '../../auth/core/Auth'
import {TermsAcceptanceRecord, TermsStatus} from '../core/_models'
import {
  getCurrentTerms,
  getTermsDocumentQueryKey,
  getTermsStatus,
  getTermsStatusQueryKey,
  getTermsVersion,
  readTermsError,
  shouldRetryTermsRead,
} from '../core/_requests'
import {hasTermsOfService} from '../core/termsAccess'
import {
  formatTermsAcceptedAt,
  formatTermsChannel,
  formatTermsDay,
  formatTermsVersion,
} from '../core/termsFormat'
import {TermsChanges, TermsDocumentView} from './TermsDocumentView'
import '../Terms.scss'

const AcceptanceLine: FC<{record: TermsAcceptanceRecord}> = ({record}) => (
  <>
    {formatTermsAcceptedAt(record)}، <bdi>{record.userName}</bdi> (
    {formatTermsChannel(record.channel)})
    {record.ipAddress && (
      <>
        {' '}
        · نشانی IP: <span dir='ltr'>{record.ipAddress}</span>
      </>
    )}
  </>
)

// Where the selected business stands: the version in force accepted, an earlier one, or none.
export const AcceptanceSummary: FC<{status: TermsStatus}> = ({status}) => {
  const business = status.companyName?.trim() || 'کسب‌وکار انتخاب‌شده'
  const current = status.acceptances.find((record) => record.version === status.currentVersion)
  const latest = status.acceptances[0]
  const history = status.acceptances.filter((record) => record !== current)

  return (
    <section className='mp-terms-acceptance' aria-label='وضعیت پذیرش کسب‌وکار'>
      {current ? (
        <div className='mp-terms-acceptance__state is-accepted' role='status'>
          <i className='las la-check-circle' aria-hidden='true' />
          <div>
            <strong>
              {formatTermsVersion(status.currentVersion)} برای «<bdi>{business}</bdi>» پذیرفته شده
              است.
            </strong>
            <small>
              <AcceptanceLine record={current} />
            </small>
          </div>
        </div>
      ) : (
        <div className='mp-terms-acceptance__state is-pending' role='status'>
          <i className='las la-hourglass-half' aria-hidden='true' />
          <div>
            <strong>
              {formatTermsVersion(status.currentVersion)} هنوز برای «<bdi>{business}</bdi>» پذیرفته
              نشده است.
            </strong>
            {latest && (
              <small>
                آخرین پذیرش: {formatTermsVersion(latest.version)}،{' '}
                <AcceptanceLine record={latest} />
              </small>
            )}
          </div>
        </div>
      )}
      {current && history.length > 0 && (
        <details className='mp-terms-acceptance__history'>
          <summary>پذیرش نسخه‌های پیشین</summary>
          <ul>
            {history.map((record) => (
              <li key={record.version}>
                {formatTermsVersion(record.version)}: <AcceptanceLine record={record} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

/**
 * The terms for reading: the version in force (or an earlier one), what changed, and for a
 * signed-in user of a business, whether and when that business accepted it. Used by the window
 * (TermsViewerModal) and by «حساب کاربری › قوانین و مقررات».
 */
export const TermsReader: FC = () => {
  const {currentUser} = useAuth()
  const [version, setVersion] = useState<number>()
  const userId = Number(currentUser?.id || 0)
  const companyId = Number(currentUser?.cpi || 0)

  const document = useQuery(
    getTermsDocumentQueryKey(version),
    ({signal}) => (version ? getTermsVersion(version, signal) : getCurrentTerms(signal)),
    {staleTime: 10 * 60 * 1000, keepPreviousData: true, retry: shouldRetryTermsRead}
  )
  const status = useQuery(
    getTermsStatusQueryKey(userId, companyId),
    ({signal}) => getTermsStatus(signal),
    {
      enabled: Boolean(currentUser && companyId > 0 && hasTermsOfService(currentUser)),
      staleTime: 60 * 1000,
      retry: shouldRetryTermsRead,
    }
  )

  const terms = document.data
  const versions = terms?.versions || []

  return (
    <>
      {status.data?.available && status.data.companyId === companyId && (
        <AcceptanceSummary status={status.data} />
      )}

      {versions.length > 1 && (
        <div className='mp-terms-versions'>
          <label htmlFor='mp-terms-version'>نسخه</label>
          <select
            id='mp-terms-version'
            className='form-select form-select-sm'
            value={terms?.version ?? ''}
            onChange={(event) => {
              const next = Number(event.target.value)
              setVersion(versions[0]?.version === next ? undefined : next)
            }}
          >
            {versions.map((item, index) => (
              <option key={item.version} value={item.version}>
                {formatTermsVersion(item.version)} — {formatTermsDay(item.effectiveDay)}
                {index === 0 ? ' (نسخه فعلی)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {document.isLoading && (
        <div className='mp-terms-dialog__state' role='status'>
          <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
          در حال بارگذاری متن قوانین و مقررات…
        </div>
      )}
      {document.isError && !terms && (
        <div className='alert alert-danger d-flex align-items-center gap-3' role='alert'>
          <span className='flex-grow-1'>
            {readTermsError(
              document.error,
              'متن قوانین و مقررات بارگذاری نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'
            )}
          </span>
          <button type='button' className='btn btn-sm btn-light' onClick={() => document.refetch()}>
            تلاش دوباره
          </button>
        </div>
      )}
      {terms && (
        <>
          {!terms.isCurrent && (
            <div className='alert alert-warning mb-5' role='note'>
              این {formatTermsVersion(terms.version)} است و دیگر اعتبار ندارد. نسخه فعلی را از فهرست
              «نسخه» ببینید.
            </div>
          )}
          <TermsChanges document={terms} />
          <TermsDocumentView document={terms} />
        </>
      )}
    </>
  )
}
